import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Homepage from './Homepage';
import TimestampConverter from './TimestampConverter';
import UrlEncoderDecoder from './UrlEncoderDecoder';
import StringStats from './StringStats';
import StringDiff from './StringDiff';
import JsonFormatter from './JsonFormatter';
import Base64Codec from './Base64Codec';
import HashCalculator from './HashCalculator';
import JwtParser from './JwtParser';
import CronParser from './CronParser';
import RegexTester from './RegexTester';
import { AboutPage, ContactPage, PrivacyPage } from './SiteInfoPages';

const SUPPORTED_LANGS = ['zh', 'en', 'es', 'ko', 'ja'] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const LANG_TO_LOCALE: Record<SupportedLang, 'zh-CN' | 'en-US' | 'es-ES' | 'ko-KR' | 'ja-JP'> = {
  zh: 'zh-CN',
  en: 'en-US',
  es: 'es-ES',
  ko: 'ko-KR',
  ja: 'ja-JP',
};

const LANG_TO_OG_LOCALE: Record<SupportedLang, string> = {
  zh: 'zh_CN',
  en: 'en_US',
  es: 'es_ES',
  ko: 'ko_KR',
  ja: 'ja_JP',
};

const getShortLangFromI18n = (lng: string | undefined): SupportedLang => {
  if (lng?.toLowerCase().startsWith('zh')) {
    return 'zh';
  }
  if (lng?.toLowerCase().startsWith('es')) {
    return 'es';
  }
  if (lng?.toLowerCase().startsWith('ko')) {
    return 'ko';
  }
  if (lng?.toLowerCase().startsWith('ja')) {
    return 'ja';
  }
  return 'en';
};

const isSupportedLang = (value: string | undefined): value is SupportedLang =>
  value === 'zh' || value === 'en' || value === 'es' || value === 'ko' || value === 'ja';

const stripLangPrefix = (pathname: string): string => {
  const stripped = pathname.replace(/^\/(zh|en|es|ko|ja)(?=\/|$)/, '');
  return stripped || '/';
};

const buildLangPath = (lang: SupportedLang, subPath: string): string => {
  const normalized = subPath.startsWith('/') ? subPath : `/${subPath}`;
  return normalized === '/' ? `/${lang}` : `/${lang}${normalized}`;
};

const SEO_CONFIG: Record<string, { titleKey: string; descriptionKey: string }> = {
  '/': {
    titleKey: 'seo.home.title',
    descriptionKey: 'seo.home.description',
  },
  '/timestamp-converter': {
    titleKey: 'seo.timestampConverter.title',
    descriptionKey: 'seo.timestampConverter.description',
  },
  '/url-encoder-decoder': {
    titleKey: 'seo.urlCodec.title',
    descriptionKey: 'seo.urlCodec.description',
  },
  '/string-stats': {
    titleKey: 'seo.stringStats.title',
    descriptionKey: 'seo.stringStats.description',
  },
  '/string-diff': {
    titleKey: 'seo.stringDiff.title',
    descriptionKey: 'seo.stringDiff.description',
  },
  '/json-formatter': {
    titleKey: 'seo.jsonFormatter.title',
    descriptionKey: 'seo.jsonFormatter.description',
  },
  '/base64-codec': {
    titleKey: 'seo.base64Codec.title',
    descriptionKey: 'seo.base64Codec.description',
  },
  '/hash-calculator': {
    titleKey: 'seo.hashCalculator.title',
    descriptionKey: 'seo.hashCalculator.description',
  },
  '/jwt-parser': {
    titleKey: 'seo.jwtParser.title',
    descriptionKey: 'seo.jwtParser.description',
  },
  '/cron-parser': {
    titleKey: 'seo.cronParser.title',
    descriptionKey: 'seo.cronParser.description',
  },
  '/regex-tester': {
    titleKey: 'seo.regexTester.title',
    descriptionKey: 'seo.regexTester.description',
  },
  '/about': {
    titleKey: 'seo.about.title',
    descriptionKey: 'seo.about.description',
  },
  '/contact': {
    titleKey: 'seo.contact.title',
    descriptionKey: 'seo.contact.description',
  },
  '/privacy': {
    titleKey: 'seo.privacy.title',
    descriptionKey: 'seo.privacy.description',
  },
};

const RouteMeta: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const shortLang = getShortLangFromI18n(i18n.resolvedLanguage);
    const locale = LANG_TO_LOCALE[shortLang];
    const subPath = stripLangPrefix(location.pathname);
    const currentMeta = SEO_CONFIG[subPath] ?? SEO_CONFIG['/'];
    document.title = t(currentMeta.titleKey);
    document.documentElement.lang = locale;

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement('meta');
      descriptionTag.setAttribute('name', 'description');
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute('content', t(currentMeta.descriptionKey));

    const base = window.location.origin;
    const canonicalHref = `${base}${buildLangPath(shortLang, subPath)}`;
    const zhHref = `${base}${buildLangPath('zh', subPath)}`;
    const enHref = `${base}${buildLangPath('en', subPath)}`;
    const esHref = `${base}${buildLangPath('es', subPath)}`;
    const koHref = `${base}${buildLangPath('ko', subPath)}`;
    const jaHref = `${base}${buildLangPath('ja', subPath)}`;
    const defaultHref = `${base}${buildLangPath('en', subPath)}`;
    const title = t(currentMeta.titleKey);
    const description = t(currentMeta.descriptionKey);
    const ogLocale = LANG_TO_OG_LOCALE[shortLang];

    const upsertLink = (selector: string, attrs: Record<string, string>) => {
      let link = document.head.querySelector(selector) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        document.head.appendChild(link);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        link!.setAttribute(key, value);
      });
    };

    const upsertMeta = (selector: string, attrs: Record<string, string>) => {
      let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        document.head.appendChild(meta);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        meta!.setAttribute(key, value);
      });
    };

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalHref });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: ogLocale });
    document.head.querySelectorAll('meta[property="og:locale:alternate"][data-dynamic="true"]').forEach((el) => el.remove());
    Object.entries(LANG_TO_OG_LOCALE)
      .filter(([lang]) => lang !== shortLang)
      .forEach(([, localeCode]) => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:locale:alternate');
        meta.setAttribute('content', localeCode);
        meta.setAttribute('data-dynamic', 'true');
        document.head.appendChild(meta);
      });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalHref });
    upsertLink('link[rel="alternate"][hreflang="zh-CN"]', {
      rel: 'alternate',
      hrefLang: 'zh-CN',
      href: zhHref,
    });
    upsertLink('link[rel="alternate"][hreflang="en"]', {
      rel: 'alternate',
      hrefLang: 'en',
      href: enHref,
    });
    upsertLink('link[rel="alternate"][hreflang="es"]', {
      rel: 'alternate',
      hrefLang: 'es',
      href: esHref,
    });
    upsertLink('link[rel="alternate"][hreflang="ko"]', {
      rel: 'alternate',
      hrefLang: 'ko',
      href: koHref,
    });
    upsertLink('link[rel="alternate"][hreflang="ja"]', {
      rel: 'alternate',
      hrefLang: 'ja',
      href: jaHref,
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hrefLang: 'x-default',
      href: defaultHref,
    });
  }, [location.pathname, i18n.resolvedLanguage, t]);

  return null;
};

const LanguageSync: React.FC = () => {
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const shortLang = isSupportedLang(lang) ? lang : getShortLangFromI18n(i18n.resolvedLanguage);
    const subPath = stripLangPrefix(location.pathname);

    if (!isSupportedLang(lang)) {
      navigate(buildLangPath(shortLang, subPath), { replace: true });
      return;
    }

    const targetLocale = LANG_TO_LOCALE[lang];
    if (i18n.resolvedLanguage !== targetLocale) {
      void i18n.changeLanguage(targetLocale);
    }
  }, [lang, location.pathname, navigate, i18n]);

  return null;
};

const LanguageRedirect: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const shortLang = getShortLangFromI18n(i18n.resolvedLanguage);
  const subPath = stripLangPrefix(location.pathname);
  return <Navigate to={buildLangPath(shortLang, subPath)} replace />;
};

const AppShell: React.FC = () => {
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main
        style={{
          marginLeft: '250px',
          padding: '20px',
          flexGrow: 1,
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Outlet />
      </main>
    </div>
  );
};

const Layout: React.FC = () => {
  return (
    <Router>
      <RouteMeta />
      <Routes>
        <Route path="/:lang" element={<><LanguageSync /><AppShell /></>}>
          <Route index element={<Homepage />} />
          <Route path="timestamp-converter" element={<TimestampConverter />} />
          <Route path="url-encoder-decoder" element={<UrlEncoderDecoder />} />
          <Route path="string-stats" element={<StringStats />} />
          <Route path="string-diff" element={<StringDiff />} />
          <Route path="json-formatter" element={<JsonFormatter />} />
          <Route path="base64-codec" element={<Base64Codec />} />
          <Route path="hash-calculator" element={<HashCalculator />} />
          <Route path="jwt-parser" element={<JwtParser />} />
          <Route path="cron-parser" element={<CronParser />} />
          <Route path="regex-tester" element={<RegexTester />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
        </Route>
        <Route path="*" element={<LanguageRedirect />} />
      </Routes>
    </Router>
  );
};

export default Layout; 